import { SiteAnnouncementFormData } from 'core/siteAnnouncement/SiteAnnouncement';
import { useFormikContext } from 'formik';
import { useCallback, useState, RefObject } from 'react';
import i18n from 'i18n';
import { Modal } from 'components/Modal/Modal';
import { UrlInputField } from 'components/form/field/UrlInputField';
import { validateUrl } from 'utils/ValidateUtils';
import { Button } from 'react-bootstrap';
import styles from './addUrlModal.module.scss';

export const AddUrlModal: React.FC<{
  contentRef: RefObject<HTMLTextAreaElement>;
}> = ({ contentRef }) => {
  const { values, setFieldValue } =
    useFormikContext<SiteAnnouncementFormData>();
  const content = values.content;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string>();

  const onUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value;
    setUrl(newUrl);
    setUrlError(validateUrl(newUrl));
  }, []);

  const showAddUrlModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeAddUrlModal = useCallback(() => {
    setShowModal(false);
    setUrl('');
  }, []);

  const handleAddUrl = useCallback((url: string) => {
    const pos = contentRef.current
      ? contentRef.current.selectionStart
      : undefined;
    if (pos === undefined) {
      return;
    }
    const newContent = `${content.slice(0, pos)}{${url}}${content.slice(pos)}`;
    setFieldValue('content', newContent);
  }, [contentRef, content, setFieldValue]);

  const onConfirm = useCallback(() => {
    handleAddUrl(url);
    closeAddUrlModal();
  }, [handleAddUrl, url, closeAddUrlModal]);

  return (
    <>
      {
        showModal && (
          <Modal
            className={styles.addUrlModal}
            title={i18n.t<string>('addUrlModal.title')}
            dismiss={closeAddUrlModal}
            primaryButton={{
              title: i18n.t<string>('common.buttons.confirm'),
              callback: onConfirm,
              disabled: !!urlError
            }}
            secondaryButton={{
              title: i18n.t<string>('common.buttons.cancel'),
              callback: closeAddUrlModal
            }}
          >
            <UrlInputField
              label={i18n.t<string>('addUrlModal.labels.clickUrl')}
              name='clickUrl'
              value={url}
              onChange={onUrlChange}
              fieldContentWidth={320}
              error={urlError}
            />
          </Modal>
        )
      }
      <Button
        variant='secondary'
        size='sm'
        onClick={showAddUrlModal}
      >
        {i18n.t<string>('addUrlModal.labels.addUrl')}
      </Button>
    </>
  );
};

export const renderAddUrlModal = (
  contentRef: RefObject<HTMLTextAreaElement>
) => {
  return <AddUrlModal contentRef={contentRef} />;
};
